/**
 * Utility per la formattazione uniforme delle date nei grafici ApexCharts
 * Formato standard: gg/mm/aaaa hh:mm
 * Supporta timezone (es. da store) per la visualizzazione.
 *
 * Design Pattern: Factory Pattern per creare configurazioni standard
 */

import { formatDate, formatDateTime, formatTime } from './dateFormatter';
import { formatDateDisplay, formatDateTimeDisplay, formatTimeDisplay } from './dateTimezone';

/**
 * Restituisce le funzioni di formattazione (con o senza timezone).
 * @param {string} [timezone] - Se presente, formatta in questa timezone per display
 */
function getFormatters(timezone) {
    if (timezone) {
        return {
            formatDate: (value) => formatDateDisplay(value, timezone) || value,
            formatDateTime: (value) => formatDateTimeDisplay(value, timezone) || value,
            formatTime: (value) => formatTimeDisplay(value, timezone) || value,
        };
    }
    return {
        formatDate: (value) => formatDate(value) || value,
        formatDateTime: (value) => formatDateTime(value) || value,
        formatTime: (value) => formatTime(value) || value,
    };
}

/**
 * Factory per creare configurazioni ApexCharts standardizzate
 * Implementa il Factory Pattern per generare configurazioni uniforme
 * @param {string} [timezone] - Timezone per formattazione date (es. da store)
 */
class ChartConfigFactory {
    constructor(timezone) {
        this.timezone = timezone;
        this.f = getFormatters(timezone);
    }
    /**
     * Crea una configurazione tooltip standard per date
     * @param {Object} customOptions - Opzioni personalizzate da unire
     * @returns {Object} Configurazione tooltip
     */
    createDateTooltip(customOptions = {}) {
        const baseConfig = {
            x: {
                formatter: (value) => {
                    return this.f.formatDateTime(value) || value;
                }
            }
        };
        
        return this.merge(baseConfig, customOptions);
    }
    
    createTimeTooltip(customOptions = {}) {
        const baseConfig = {
            x: {
                formatter: (value) => {
                    return this.f.formatTime(value) || value;
                }
            }
        };
        
        return this.merge(baseConfig, customOptions);
    }
    
    createDateTimeXAxis(customOptions = {}) {
        const baseConfig = {
            type: 'datetime',
            labels: {
                formatter: (value, timestamp) => {
                    const v = timestamp ?? value;
                    return this.f.formatDateTime(v) || value;
                },
                style: {
                    colors: '#213A8F'
                }
            }
        };
        
        return this.merge(baseConfig, customOptions);
    }
    
    /**
     * Crea una configurazione xaxis per solo date
     * @param {Object} customOptions - Opzioni personalizzate
     * @returns {Object} Configurazione xaxis
     */
    createDateXAxis(customOptions = {}) {
        const baseConfig = {
            type: 'datetime',
            labels: {
                formatter: (value, timestamp) => {
                    const v = timestamp ?? value;
                    return this.f.formatDate(v) || value;
                },
                style: {
                    colors: '#213A8F'
                }
            }
        };
        
        return this.merge(baseConfig, customOptions);
    }
    
    /**
     * Merge di due oggetti (deep merge semplificato)
     * @param {Object} target - Oggetto target
     * @param {Object} source - Oggetto sorgente
     * @returns {Object} Oggetto merged
     */
    merge(target, source) {
        const output = { ...target };
        
        if (this.isObject(target) && this.isObject(source)) {
            Object.keys(source).forEach(key => {
                if (this.isObject(source[key])) {
                    if (!(key in target)) {
                        Object.assign(output, { [key]: source[key] });
                    } else {
                        output[key] = this.merge(target[key], source[key]);
                    }
                } else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }
        
        return output;
    }
    
    /**
     * Verifica se un valore è un oggetto
     * @param {*} item - Valore da verificare
     * @returns {boolean}
     */
    isObject(item) {
        return item && typeof item === 'object' && !Array.isArray(item);
    }
}

// Singleton factory (senza timezone per backward compatibility)
const chartConfigFactory = new ChartConfigFactory();

/**
 * Crea una configurazione completa per grafici con date
 * @param {Object} options - Opzioni personalizzate (options.timezone per formattazione display)
 * @returns {Object} Configurazione ApexCharts completa
 */
export function createDateChartConfig(options = {}) {
    const { timezone, ...rest } = options;
    const factory = timezone ? new ChartConfigFactory(timezone) : chartConfigFactory;
    const baseConfig = {
        chart: {
            fontFamily: 'inherit',
            locales: [{
                name: 'it',
                options: {
                    months: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                             'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
                    shortMonths: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 
                                  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
                    days: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
                    shortDays: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab']
                }
            }],
            defaultLocale: 'it'
        },
        xaxis: factory.createDateTimeXAxis(),
        tooltip: factory.createDateTooltip()
    };
    
    return chartConfigFactory.merge(baseConfig, rest);
}

/**
 * Crea una configurazione per grafici con solo date (senza ora)
 * @param {Object} options - Opzioni (options.timezone per formattazione display)
 * @returns {Object} Configurazione ApexCharts
 */
export function createDateOnlyChartConfig(options = {}) {
    const { timezone, ...rest } = options;
    const factory = timezone ? new ChartConfigFactory(timezone) : chartConfigFactory;
    const baseConfig = {
        chart: {
            fontFamily: 'inherit',
            locales: [{
                name: 'it',
                options: {
                    months: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                             'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
                    shortMonths: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 
                                  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
                    days: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
                    shortDays: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab']
                }
            }],
            defaultLocale: 'it'
        },
        xaxis: factory.createDateXAxis(),
        tooltip: {
            x: {
                formatter: (value) => factory.f.formatDate(value) || value
            }
        }
    };
    
    return chartConfigFactory.merge(baseConfig, rest);
}

/**
 * Formatter personalizzato per tooltip con data e ora (usa timezone se passata)
 * @param {string} [timezone]
 */
export function dateTimeTooltipFormatter(timezone) {
    const f = getFormatters(timezone);
    return (value) => f.formatDateTime(value) || value;
}

/**
 * Formatter personalizzato per tooltip con solo data
 */
export function dateTooltipFormatter(timezone) {
    const f = getFormatters(timezone);
    return (value) => f.formatDate(value) || value;
}

/**
 * Formatter personalizzato per tooltip con solo ora
 */
export function timeTooltipFormatter(timezone) {
    const f = getFormatters(timezone);
    return (value) => f.formatTime(value) || value;
}

/**
 * Formatter per etichette xaxis con data e ora
 */
export function dateTimeAxisFormatter(timezone) {
    const f = getFormatters(timezone);
    return (value, timestamp) => {
        const v = timestamp ?? value;
        return f.formatDateTime(v) || value;
    };
}

/**
 * Formatter per etichette xaxis con solo data
 */
export function dateAxisFormatter(timezone) {
    const f = getFormatters(timezone);
    return (value, timestamp) => {
        const v = timestamp ?? value;
        return f.formatDate(v) || value;
    };
}

/**
 * Formatter per etichette xaxis con solo ora
 */
export function timeAxisFormatter(timezone) {
    const f = getFormatters(timezone);
    return (value, timestamp) => {
        const v = timestamp ?? value;
        return f.formatTime(v) || value;
    };
}

// Export formatter functions senza timezone per backward compatibility (usano ora locale)
export const dateTimeTooltipFormatterLegacy = (value) => formatDateTime(value) || value;
export const dateTooltipFormatterLegacy = (value) => formatDate(value) || value;
export const timeTooltipFormatterLegacy = (value) => formatTime(value) || value;
export const dateTimeAxisFormatterLegacy = (value, timestamp) => {
    const v = timestamp ?? value;
    return formatDateTime(v) || value;
};
export const dateAxisFormatterLegacy = (value, timestamp) => {
    const v = timestamp ?? value;
    return formatDate(v) || value;
};
export const timeAxisFormatterLegacy = (value, timestamp) => {
    const v = timestamp ?? value;
    return formatTime(v) || value;
};

/**
 * Applica la formattazione standard alle opzioni del grafico esistenti
 * @param {Object} chartOptions - Opzioni grafico esistenti
 * @param {string} formatType - Tipo di formato ('datetime', 'date', 'time')
 * @param {string} [timezone] - Timezone per display (es. da store)
 * @returns {Object} Opzioni aggiornate
 */
export function applyStandardDateFormat(chartOptions, formatType = 'datetime', timezone) {
    const options = { ...chartOptions };
    
    if (!options.tooltip) options.tooltip = {};
    if (!options.tooltip.x) options.tooltip.x = {};
    
    const formatters = getFormatters(timezone);
    switch (formatType) {
        case 'datetime':
            options.tooltip.x.formatter = (value) => formatters.formatDateTime(value) || value;
            break;
        case 'date':
            options.tooltip.x.formatter = (value) => formatters.formatDate(value) || value;
            break;
        case 'time':
            options.tooltip.x.formatter = (value) => formatters.formatTime(value) || value;
            break;
    }
    
    if (options.xaxis && options.xaxis.type === 'datetime') {
        if (!options.xaxis.labels) options.xaxis.labels = {};
        const axisFormatters = getFormatters(timezone);
        switch (formatType) {
            case 'datetime':
                options.xaxis.labels.formatter = (value, timestamp) => axisFormatters.formatDateTime(timestamp ?? value) || value;
                break;
            case 'date':
                options.xaxis.labels.formatter = (value, timestamp) => axisFormatters.formatDate(timestamp ?? value) || value;
                break;
            case 'time':
                options.xaxis.labels.formatter = (value, timestamp) => axisFormatters.formatTime(timestamp ?? value) || value;
                break;
        }
    }
    
    if (options.tooltip.x.format) delete options.tooltip.x.format;
    if (options.xaxis && options.xaxis.labels && options.xaxis.labels.format) {
        delete options.xaxis.labels.format;
    }
    
    return options;
}

/**
 * Applica la timezone alle label e ai tooltip di uno o più grafici ApexCharts.
 * Da usare in ogni view che ha grafici con date/ora (chiamare dopo aver costruito i chartOptions
 * e quando cambia la timezone dello store).
 *
 * @param {Record<string, object>} chartsMap - Oggetto con chiavi nomi e valori chartOptions (es. { chartOptions_bin: this.chartOptions_bin })
 * @param {string} timezone - Timezone da store (es. this.$store.state.timezone || 'Europe/Rome')
 * @param {{ datetimeAxisKeys?: string[] }} opts - datetimeAxisKeys: chiavi dei grafici con xaxis type 'datetime' (per questi si usa formatTime/formatDateTime)
 */
export function applyTimezoneToApexCharts(chartsMap, timezone, opts = {}) {
    if (!chartsMap || !timezone) return;
    const tz = timezone || 'Europe/Rome';
    const datetimeKeys = new Set(opts.datetimeAxisKeys || []);
    const formatCategory = (value) => {
        if (value == null || value === '') return '';
        const s = String(value).trim();
        if (s.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(s)) return formatDateDisplay(s, tz) || value;
        return formatDateTimeDisplay(s, tz) || formatDateDisplay(s, tz) || value;
    };
    const formatDatetimeLabel = (value, timestamp) => formatTimeDisplay(timestamp != null ? timestamp : value, tz) || value;
    const formatTooltipX = (value) => formatDateTimeDisplay(value, tz) || value;

    Object.keys(chartsMap).forEach((key) => {
        const chart = chartsMap[key];
        if (!chart || !chart.xaxis) return;
        const isDatetimeAxis = chart.xaxis.type === 'datetime' || datetimeKeys.has(key);
        if (chart.xaxis.labels) {
            if (isDatetimeAxis) {
                chart.xaxis.labels.formatter = (value, timestamp) => formatDatetimeLabel(value, timestamp) || value;
            } else {
                chart.xaxis.labels.formatter = (value) => formatCategory(value) || value;
            }
        }
        if (chart.tooltip && chart.tooltip.x) {
            chart.tooltip.x.formatter = formatTooltipX;
        }
    });
}

/**
 * Esporta la factory per usi avanzati
 */
export { chartConfigFactory };

/**
 * Export default per compatibilità
 */
export default {
    createDateChartConfig,
    createDateOnlyChartConfig,
    dateTimeTooltipFormatter,
    dateTooltipFormatter,
    timeTooltipFormatter,
    dateTimeAxisFormatter,
    dateAxisFormatter,
    timeAxisFormatter,
    applyStandardDateFormat,
    applyTimezoneToApexCharts,
    chartConfigFactory
};
